import { useMemo, useState, useEffect } from 'react';
import { PC_CONSOLE_COMBINE_ENTITY_TYPE } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import {
  fetchGameListPermissions,
  fetchGameListDashboardPermissions,
} from '@/pages/home/services/search';
import {
  getIsOpinionFromType,
  getItemDashboardTips,
} from '@/pages/intelligence/utils/topchartsUtils';
import moment from 'moment';
import {
  getDetailCombinedIds,
  getDemographicsAppids,
  addTrueDataFeedback,
} from '@/pages/intelligence/services/topcharts';
import { getTrendsList } from '@intelligence/services/trendingames';
import { COMBINEID_REG } from '@intelligence/utils/constant';

// 游戏详情公共hooks
const useGamesDetailsModel = (props: any) => {
  const { entityType, gameId, basicInfo, topchartsState, appIdList, initNote } = props || {};

  const [showOpinion, setShowOpinion] = useState(false);
  const [opinionId, setOpinionId] = useState('');
  // 经分game_code
  const [dashboardGameCode, setDashboardGameCode] = useState('');
  // 是否有此游戏经分权限，并弹窗
  const [ifShowDashboardTips, setIfShowDashboardTips] = useState(false);
  // Overlap-loading
  const [overlapAppIdsLoading, setOverlapAppIdsLoading] = useState(false);
  const [combineGameIdList, setCombineGameIdList] = useState<
    { id: string; entity_type: string; overlap_ids: any }[]
  >([]);
  // demographics判断字段
  const [isDemographics, setIsDemographics] = useState(false);
  /** 有Demographics数据的ids */
  const [demographicsIdsList, setDemographicsIdsList] = useState([]);
  /** getIsDemographics 请求是否已完成（含 appids 为空提前 return 的情况） */
  const [demographicsIdsLoaded, setDemographicsIdsLoaded] = useState(false);
  // Overlap页签的appIds-Steam
  const [appOverlapSteamId, setAppOverlapSteamId] = useState<string>('');
  // 是否禁用OverlapSteam页签
  const [isDisabledSteamOverlap, setIsDisabledSteamOverlap] = useState(false);
  // 是否禁用data report
  const [isDisabledDataReport, setIsDisabledDataReport] = useState(true);
  const [dataReportGameId, setDataReportGameId] = useState('');
  // 控制feedback接口是否返回
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);

  const defaultActiveSegment = useMemo(() => {
    switch (entityType) {
      case 'mobile':
        return 'activeusers';
      case 'pc':
        return 'pcu';
      case 'console':
      case PC_CONSOLE_COMBINE_ENTITY_TYPE:
        return '';
    }
  }, [entityType]);

  // 获取舆情权限数据
  const getGameOpinionsPermissions = async (combineGameIdList: any[]) => {
    // 获取对应游戏类型ID数组参数
    const idList = combineGameIdList?.map((item) => item.id);
    const paramsOpinions = {
      edition_unified_ids: idList,
    };

    // 获取到所有游戏类型对应舆情权限
    try {
      const resultOpinion = await fetchGameListPermissions(paramsOpinions);
      const { list: opinionList = [] } = resultOpinion?.data || {};
      // 根据当前游戏类型，获取游戏详情舆情是否展示，即是否有舆情数据，并返回对应游戏类型id
      const isOpinionObj = getIsOpinionFromType(opinionList, combineGameIdList, entityType);
      setShowOpinion(isOpinionObj?.opinion_data ?? false);
      setOpinionId(isOpinionObj?.edition_unified_id ?? '');
    } catch (err) {
      setShowOpinion(false);
      setOpinionId('');
    }
  };

  // 获取经分权限数据
  const getGameDashboardPermissions = async (gameId: string) => {
    const paramsDashboard = {
      unified_ids: [gameId],
    };
    // 获取到所有游戏类型对应舆情权限
    try {
      const resultDashboard = await fetchGameListDashboardPermissions(paramsDashboard);

      const { list: dashboardList = [] } = resultDashboard?.data || {};
      // 根据游戏是否有经分权限，并且当天没有过纪录，且总弹窗数没到5次，即会弹窗是否跳经分
      if (Array.isArray(dashboardList) && dashboardList?.length > 0) {
        // 如果有经分权限
        if (dashboardList[0].has_permissions) {
          setDashboardGameCode(dashboardList[0]?.game_code);
          const dashboardTips = getItemDashboardTips();
          const today = moment().format('YYYY-MM-DD');
          const { ifShow = true, openDay = '', openTotal = 0 } = dashboardTips;
          // 如果本缓存数据是要弹窗，并且弹窗记录不是今天，并且弹窗次数没超过5次，弹窗，并添加新记录
          if (ifShow && openDay !== today && openTotal < 5) {
            setIfShowDashboardTips(true);
          }
        }
      }
    } catch (err) {}
  };

  // 统一查询overlap权限及id
  const getCombinedGameId = async (entityType: string, gameId: string) => {
    setOverlapAppIdsLoading?.(true);
    // 添加游戏ID校验规则
    if (COMBINEID_REG.test(gameId)) {
      try {
        const result = await getDetailCombinedIds({
          entity_type: entityType,
          id: gameId,
          b_get_overLap_id: entityType !== 'console',
        });
        const combineGameListTemp = result?.data?.filter(
          (item: any) => item?.id && item?.id !== 'None' && item?.id !== '',
        );
        setCombineGameIdList(combineGameListTemp);
        if (combineGameListTemp.filter((item: any) => item.entity_type === 'combine')?.length > 0) {
          initNote?.();
        }
        if (Array.isArray(combineGameListTemp) && combineGameListTemp?.length > 0) {
          // 获取此游戏所有类型的舆情权限
          getGameOpinionsPermissions(combineGameListTemp);
          // 获取此游戏对应经分权限
          getGameDashboardPermissions(gameId);
        }
      } catch (err) {
        setCombineGameIdList([]);
      } finally {
        setOverlapAppIdsLoading?.(false);
      }
    } else {
      setCombineGameIdList([]);
      setOverlapAppIdsLoading?.(false);
    }
  };

  const getIsDemographics = async (appids: string[]) => {
    /** 接口返回数据为空数组则置灰 */
    if (!Array.isArray(appids) || (Array.isArray(appids) && appids?.length < 1)) {
      setDemographicsIdsLoaded(true);
      return;
    }
    try {
      const res = await getDemographicsAppids({
        app_ids: appids,
        source: 'sensortower',
      });
      if (res?.data?.appids?.length > 0) {
        setIsDemographics(true);
        setDemographicsIdsList(res?.data?.appids);
      } else {
        setIsDemographics(false);
        setDemographicsIdsList([]);
      }
    } catch {
      setIsDemographics(false);
      setDemographicsIdsList([]);
    } finally {
      setDemographicsIdsLoaded(true);
    }
  };

  useEffect(() => {
    if (Array.isArray(combineGameIdList) && combineGameIdList?.length > 0) {
      // pc与融合id，用同一个Overlap页签的appIds-Steam的id，console没有
      const pcIdObj: any = combineGameIdList?.find((item: any) => item.entity_type === 'pc') || {};
      if (Array.isArray(pcIdObj.overlap_ids) && pcIdObj.overlap_ids?.length > 0) {
        setAppOverlapSteamId(pcIdObj.overlap_ids[0]);
        setIsDisabledSteamOverlap(pcIdObj.overlap_ids[0] === '');
      } else {
        setIsDisabledSteamOverlap(true);
      }
    }
  }, [combineGameIdList, entityType]);

  // 与游戏头部信息一起查询，相关Data Report的权限
  const getTrendGameList = async () => {
    const list = await getTrendsList({});
    if (Array.isArray(list)) {
      const trendGame = list.find((item) => item?.unified_id === gameId);
      if (trendGame) {
        setIsDisabledDataReport(false);
        setDataReportGameId(trendGame?.unified_id);
      } else {
        setIsDisabledDataReport(true);
      }
    }
  };

  // 拼接feedback接口需要的参数
  const getFeedbackBasicInfo = () => {
    const { id } = basicInfo;
    const { activedPageLogMes, selectedDemographicsAppid } = topchartsState;
    const pageInfo = activedPageLogMes
      ? `${activedPageLogMes?.pageId},${activedPageLogMes?.pageName},${activedPageLogMes?.pagePath}`
      : '';
    return {
      entity_type: entityType,
      game_id: id,
      app_ids:
        activedPageLogMes === 'mobile/demographics' ? [selectedDemographicsAppid] : appIdList,
      page_id: pageInfo,
    };
  };

  // feedback提交的处理函数
  const handleFeedbackSubmit = ({
    text,
    category,
    anonymity,
    noticeControl,
  }: {
    text: string;
    category: string[];
    anonymity: boolean;
    noticeControl: Function;
  }) => {
    setIsFeedbackLoading(true);
    const params = Object.assign(
      {
        category,
        content: text,
        anonymity,
      },
      getFeedbackBasicInfo(),
    );
    // 调用接口，loading是为了控制弹窗重复点击和关闭
    addTrueDataFeedback(params)
      .then(() => {
        setIsFeedbackLoading(false);
        noticeControl('success');
      })
      .catch(() => {
        setIsFeedbackLoading(false);
        noticeControl('error');
      });
  };

  return {
    defaultActiveSegment,
    getCombinedGameId,
    showOpinion,
    opinionId,
    dashboardGameCode,
    ifShowDashboardTips,
    overlapAppIdsLoading,
    combineGameIdList,
    setIfShowDashboardTips,
    getIsDemographics,
    isDemographics,
    demographicsIdsList,
    demographicsIdsLoaded,
    appOverlapSteamId,
    isDisabledSteamOverlap,
    getTrendGameList,
    isDisabledDataReport,
    dataReportGameId,
    getFeedbackBasicInfo,
    handleFeedbackSubmit,
    isFeedbackLoading,
  };
};

export default useGamesDetailsModel;
